import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';

import {
  DAY_COLUMNS,
  weekdayNarrowLabel,
  weekdayShortLabel,
  type DayColumnKey,
} from '@/domain/templates/day-of-week';

/**
 * Locale-aware short weekday label keyed by the canonical column key
 * (Mon → Sun). Resolves through Luxon's CLDR data via
 * `weekdayShortLabel`, which is correct in every locale — far better
 * than round-tripping seven hardcoded `<Trans>Mon</Trans>` strings
 * through Azure Translator, which mistranslates "Mon" as "my" in
 * French and produces other locale-wrong abbreviations.
 *
 * Reads the active Lingui locale from `useLingui` so a runtime
 * locale switch picks up the right abbreviation without the caller
 * having to thread the locale through.
 */
export function DayLabel({
  columnKey,
  variant = 'short',
}: {
  columnKey: DayColumnKey;
  /** `short` = 3-letter (Mon), `narrow` = single-letter (M) for round toggles. */
  variant?: 'short' | 'narrow';
}) {
  const { i18n } = useLingui();
  const dayOfWeek = DAY_COLUMNS.find((c) => c.key === columnKey)?.dayOfWeek ?? 0;
  const label = useMemo(
    () =>
      variant === 'narrow'
        ? weekdayNarrowLabel(dayOfWeek, i18n.locale)
        : weekdayShortLabel(dayOfWeek, i18n.locale),
    [dayOfWeek, i18n.locale, variant],
  );
  return <>{label}</>;
}
